import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

const API = 'https://cvanalyzerback.osc-fr1.scalingo.io/api/stats/barometre';

const Barometre = () => {
    const [data, setData] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        fetch(API)
            .then((res) => {
                if (!res.ok) throw new Error('unavailable');
                return res.json();
            })
            .then(setData)
            .catch(() => setError('Le baromètre n’est pas disponible pour le moment.'));
    }, []);

    const updated = data && data.updatedAt
        ? new Date(data.updatedAt).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })
        : null;

    return (
        <div className="min-h-screen bg-slate-200 pt-16">
            <Helmet>
                <title>Baromètre des scores CV — CV Analyse</title>
                <meta
                    name="description"
                    content="Moyennes anonymes des scores CV Analyse par secteur. Échantillon réel, sans données personnelles."
                />
                <link rel="canonical" href="https://www.cv-analyse.fr/barometre" />
            </Helmet>

            <div className="bg-slate-900 text-white">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
                    <p className="text-sm text-slate-400 mb-3">
                        <Link to="/" className="hover:text-white">Accueil</Link>
                        {' / '}
                        Baromètre
                    </p>
                    <h1 className="text-3xl md:text-4xl font-semibold mb-3">
                        Baromètre des scores CV
                    </h1>
                    <p className="text-slate-300 leading-relaxed">
                        Moyennes calculées sur les analyses réellement effectuées sur CV Analyse.
                        Aucun CV n’est publié. Un secteur n’apparaît que s’il compte au moins 5 notes.
                    </p>
                </div>
            </div>

            <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 space-y-6">
                {error && (
                    <p className="card-surface p-6 text-slate-600">{error}</p>
                )}

                {data && (
                    <section className="card-surface p-6 sm:p-8">
                        <p className="text-sm text-slate-500 mb-4">
                            {data.sampleSize} analyses notées
                            {data.avgScore != null ? ` · score moyen ${data.avgScore}/100` : ''}
                            {updated ? ` · mis à jour le ${updated}` : ''}
                        </p>
                        {data.sampleSize < 5 && (
                            <p className="text-slate-600 leading-relaxed">
                                L’échantillon public est encore trop petit pour afficher des moyennes par secteur.
                                La <Link to="/methode" className="text-blue-700 font-medium">méthode de notation</Link> est déjà publiée.
                            </p>
                        )}
                        {data.sectors && data.sectors.length > 0 && (
                            <table className="w-full text-left text-sm">
                                <thead>
                                    <tr className="border-b border-slate-200 text-slate-500">
                                        <th className="py-2 pr-3 font-medium">Secteur</th>
                                        <th className="py-2 pr-3 font-medium">Analyses</th>
                                        <th className="py-2 font-medium">Score moyen</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.sectors.map((row) => (
                                        <tr key={row.sector} className="border-b border-slate-100">
                                            <td className="py-2 pr-3 text-slate-900 capitalize">{row.sector}</td>
                                            <td className="py-2 pr-3 text-slate-600">{row.count}</td>
                                            <td className="py-2 text-slate-900 font-semibold">{row.avgScore}/100</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </section>
                )}

                <section className="card-surface p-6 sm:p-8">
                    <h2 className="text-xl font-semibold text-slate-900 mb-3">Comment lire ces chiffres</h2>
                    <p className="text-slate-600 leading-relaxed mb-3">
                        Un score autour de 70–85 décrit un CV déjà lisible, pas un CV « parfait ».
                        Les écarts entre secteurs viennent surtout du vocabulaire métier et de la présence de résultats chiffrés,
                        pas d’un classement des professions.
                    </p>
                    <p className="text-slate-600 leading-relaxed">
                        Détail des 5 critères : <Link to="/methode" className="text-blue-700 font-medium">méthode de notation</Link>.
                    </p>
                </section>

                <section className="cta-band text-center">
                    <h2 className="text-xl font-semibold mb-2">Comparer votre CV à cette moyenne</h2>
                    <p className="text-slate-300 text-sm mb-5">Score et 3 conseils offerts — rapport complet dès 5 €.</p>
                    <Link to="/" className="btn-primary">Voir mon score</Link>
                </section>
            </div>
        </div>
    );
};

export default Barometre;
